import React, { useState, useEffect } from 'react';
import { MapPin, Building, Globe } from 'lucide-react';
import axios from 'axios';

// Tipos de ubicaciones que podemos sugerir
export const LocationType = {
  CITY: 'city',
  COUNTRY: 'country',
  DISTRICT: 'district'
};

// Datos de ciudades de prueba
const MOCK_CITIES = [
  { id: 1, ciudad: "Lima", pais: "Perú", n_propiedades: 1245, latitude: -12.0464, longitude: -77.0428 },
  { id: 2, ciudad: "Arequipa", pais: "Perú", n_propiedades: 534, latitude: -16.4090, longitude: -71.5375 },
  { id: 3, ciudad: "Trujillo", pais: "Perú", n_propiedades: 423, latitude: -8.1116, longitude: -79.0289 },
  { id: 4, ciudad: "Chiclayo", pais: "Perú", n_propiedades: 312, latitude: -6.7763, longitude: -79.8437 },
  { id: 5, ciudad: "Cusco", pais: "Perú", n_propiedades: 298, latitude: -13.5319, longitude: -71.9675 }
];

// URL del backend
const API_URL = 'https://backend-production-1786.up.railway.app'; // Asegúrate que esta URL es correcta

const LocationSuggestions = ({ query, onSelectLocation }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [useFallback, setUseFallback] = useState(false);

  // Función para obtener el icono correspondiente al tipo de ubicación
  const getLocationIcon = (locationType) => {
    switch (locationType) {
      case LocationType.CITY:
        return Building;
      case LocationType.COUNTRY:
        return Globe;
      case LocationType.DISTRICT:
      default:
        return MapPin;
    }
  };

  // Mostrar resultados de búsqueda del mock cuando estamos en modo fallback
  const showMockResults = (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) return [];
    
    return MOCK_CITIES
      .filter(city => 
        city.ciudad.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.pais.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map(city => ({
        id: city.id,
        display: `${city.ciudad}`,
        secondaryText: city.pais,
        type: LocationType.CITY,
        value: city.ciudad,
        extraData: {
          countryCode: city.pais,
          propertyCount: city.n_propiedades,
          latitude: city.latitude,
          longitude: city.longitude
        }
      }));
  };

  // Buscar sugerencias de ciudades cuando el query cambia
  useEffect(() => {
    const searchLocations = async () => {
      // Solo buscar si hay al menos 2 caracteres
      if (!query || query.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);
      setShowSuggestions(true);
      
      // Si estamos en modo fallback, usar datos de prueba
      if (useFallback) {
        const mockResults = showMockResults(query);
        setSuggestions(mockResults);
        setShowSuggestions(mockResults.length > 0);
        setLoading(false);
        return;
      }

      try {
        console.log("Realizando petición a:", `${API_URL}/api/ciudades/search?q=${encodeURIComponent(query)}`);
        
        const response = await axios.get(`${API_URL}/api/ciudades/search?q=${encodeURIComponent(query)}`, {
          // Agregar header para debugging
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          // Timeout más largo para dar tiempo al servidor a responder
          timeout: 5000
        });
        
        console.log("Respuesta completa:", response);
        
        // Verificar estructura de la respuesta
        if (response.data && response.data.cities) {
          console.log("Ciudades encontradas:", response.data.cities);
          
          // Transformar los resultados al formato que necesitamos
          const formattedResults = response.data.cities.map(city => ({
            id: city.id,
            display: `${city.ciudad}`,
            secondaryText: city.pais,
            type: LocationType.CITY,
            value: city.ciudad,
            extraData: {
              countryCode: city.pais,
              propertyCount: city.n_propiedades,
              latitude: city.latitude,
              longitude: city.longitude
            }
          }));
          
          setSuggestions(formattedResults);
          setShowSuggestions(formattedResults.length > 0);
          setError(null);
        } else {
          console.warn("Estructura de respuesta inesperada:", response.data);
          throw new Error("Formato de respuesta inesperado");
        }
      } catch (error) {
        console.error("Error buscando ubicaciones:", error);
        
        // Activar modo fallback para futuras búsquedas
        setUseFallback(true);
        
        // Usar datos de prueba
        const mockResults = showMockResults(query);
        setSuggestions(mockResults);
        setShowSuggestions(mockResults.length > 0);
        
        // Mostrar mensaje de error apropiado
        setError("No se pudo conectar al servidor. Mostrando resultados de ejemplo.");
      } finally {
        setLoading(false);
      }
    };

    // Debounce la búsqueda para no hacer demasiadas solicitudes
    const debounceTimer = setTimeout(() => {
      searchLocations();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, useFallback]);

  return (
    <div className="py-2">
      {loading && (
        <div className="p-4 text-center text-gray-500">
          <div className="animate-pulse flex justify-center">
            <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
          </div>
          <p className="mt-2 text-sm">Buscando ubicaciones...</p>
        </div>
      )}
      
      {error && (
        <div className="p-3 bg-amber-50 border-l-4 border-amber-500">
          <p className="text-amber-700 text-sm">{error}</p>
          {useFallback && (
            <button 
              className="text-xs text-indigo-600 underline mt-1"
              onClick={() => setUseFallback(false)}
            >
              Reintentar conexión
            </button>
          )}
        </div>
      )}
      
      {suggestions.length > 0 ? (
        <ul className="max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => {
            const Icon = getLocationIcon(suggestion.type);
            
            return (
              <li
                key={`${suggestion.type}-${suggestion.id}`}
                className="cursor-pointer hover:bg-indigo-50 transition-colors"
                onClick={() => {
                  onSelectLocation(suggestion);
                  setShowSuggestions(false);
                }}
              >
                <div className="p-3 flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0">
                    <Icon className="w-5 h-5 text-indigo-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{suggestion.display}</p>
                    {suggestion.secondaryText && (
                      <p className="text-sm text-gray-500">{suggestion.secondaryText}</p>
                    )}
                    {suggestion.extraData?.propertyCount && (
                      <p className="text-xs text-indigo-600 mt-1">
                        {suggestion.extraData.propertyCount} propiedades
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : !loading && query && query.length >= 2 ? (
        <div className="p-4 text-center text-gray-500">
          <p>No se encontraron ubicaciones para "{query}"</p>
        </div>
      ) : null}
    </div>
  );
};

export default LocationSuggestions;